import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  ArrowLeftIcon,
  SparklesIcon,
  LockClosedIcon,
  CheckBadge
} from '../../lib/commonUtils';

import api from '../../api/axiosInstance';
import useAuthStore from '../../store/authStore';
import useAppStore from '../../store/appStore';
import { normalizeAgentForEdit } from '../../lib/commonUtils';

const getSubscriptionTier = (subscription) => {
  const planName = subscription?.plan?.toLowerCase() || 'free';

  if (planName.includes('enterprise')) return 'enterprise';
  if (planName.includes('premium')) return 'premium';
  if (planName.includes('pro')) return 'pro';
  if (planName.includes('starter')) return 'starter';
  return 'free';
};

const UpgradePrompt = ({ feature, currentPlan }) => (
  <div className="mt-4 p-6 controlled-glass rounded-2xl border border-yellow-500/20 bg-yellow-500/5 inner-highlight">
    <div className="flex items-start">
      <div className="p-2 bg-yellow-500/10 rounded-lg mr-4">
        <LockClosedIcon className="h-5 w-5 text-yellow-500" />
      </div>
      <div>
        <p className="text-sm font-bold text-white tracking-tight">
          {feature} is restricted
        </p>
        <p className="text-xs font-medium text-white/40 mt-1 italic leading-relaxed">
          First message customization is unavailable on your current <span className="text-yellow-500/80 font-bold not-italic capitalize">{currentPlan}</span> plan.
          Upgrade to Pro or higher to unlock this feature.
        </p>
        <Link
          to="/billing"
          className="inline-flex items-center mt-4 text-[10px] font-bold uppercase tracking-widest text-yellow-500 hover:text-yellow-400 border-b border-yellow-500/30 hover:border-yellow-500 transition-all pb-0.5"
        >
          Upgrade Plan →
        </Link>
      </div>
    </div>
  </div>
);

const AgentEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { fetchAgents, updateAgentExact, currentSubscription } = useAppStore();

  const subscriptionTier = useMemo(() =>
    getSubscriptionTier(currentSubscription),
    [currentSubscription]
  );

  const featuresAvailable = useMemo(() => {
    const tier = subscriptionTier;

    return {
      canEditFirstMessage: tier === 'free' || tier === 'pro' || tier === 'premium' || tier === 'enterprise',
    };
  }, [subscriptionTier]);

  const [loading, setLoading] = useState(true);
  const [agentType, setAgentType] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      companyName: '',
      firstMessage: '',
    },
  });

  const companyName = watch('companyName');
  const email = watch('email');
  const name = watch('name');
  const firstMessageValue = watch('firstMessage', '');

  useEffect(() => {
    let mounted = true;

    const loadAgent = async () => {
      setLoading(true);
      const loginEmail = user?.email || user?.user_email || '';

      try {
        const resOne = await api.get(`/auth/agent/get-agent/${id}`).catch((e) => ({ __err: e }));

        let agentData;

        if (!resOne.__err && resOne?.data) {
          agentData = normalizeAgentForEdit(resOne.data, loginEmail);
          setAgentType(resOne.data.type || 'customer_support');
        } else {
          const resList = await api
            .get('/auth/admin/user-agents', { params: { email: loginEmail } })
            .catch((e) => ({ __err: e }));

          if (!resList.__err) {
            const list = Array.isArray(resList?.data?.agents) ? resList.data.agents : [];
            const foundFromList =
              list.find((a) => String(a.agent_id) === String(id)) ||
              list.find((a) => String(a.id) === String(id)) ||
              list.find((a) => String(a._id) === String(id));

            if (foundFromList) {
              agentData = normalizeAgentForEdit(foundFromList, resList?.data?.user_email || loginEmail);
              setAgentType(foundFromList.type || 'customer_support');
            }
          }

          if (!agentData) {
            const resAll = await api
              .post('/analysis/training/agent-individual-analytics', {})
              .catch((e) => ({ __err: e }));

            if (!resAll.__err) {
              const list = Array.isArray(resAll?.data?.individual_results)
                ? resAll.data.individual_results
                : [];

              const foundAny =
                list.find((a) => String(a.agent_id) === String(id)) ||
                list.find((a) => String(a.id) === String(id)) ||
                list.find((a) => String(a._id) === String(id));

              if (foundAny) {
                agentData = normalizeAgentForEdit(foundAny, loginEmail);
                setAgentType(foundAny.type || 'customer_support');
              }
            }
          }

          if (!agentData) {
            throw new Error('Agent not found');
          }
        }

        if (!mounted) return;

        reset({
          name: agentData.name,
          phone: agentData.phone,
          email: agentData.email,
          companyName: agentData.companyName,
          firstMessage: agentData.firstMessage || 'Hello! I am your assistant. How may I help you?',
        });
      } catch (err) {
        if (!mounted) return;
        toast.error(err?.message || 'Failed to load agent');
        navigate('/agents', { replace: true });
        return;
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadAgent();
    return () => {
      mounted = false;
    };
  }, [id, user, reset, navigate]);

  const onSubmit = async (data) => {
    if (!featuresAvailable.canEditFirstMessage) {
      const res = await updateAgentExact({
        email: data.email,
        agent_name: data.name,
        business_name: data.companyName || undefined,
        contact_phone_number: data.phone || undefined,
      });

      if (res?.success) {
        toast.success('Agent updated successfully!');
        await fetchAgents?.();
        navigate('/agents');
      } else {
        toast.error(res?.error || 'Failed to update agent');
      }
      return;
    }

    const res = await updateAgentExact({
      email: data.email,
      agent_name: data.name,
      first_message: data.firstMessage,
      business_name: data.companyName || undefined,
      contact_phone_number: data.phone || undefined,
    });

    if (res?.success) {
      toast.success('Agent updated successfully!');
      await fetchAgents?.();
      navigate('/agents');
    } else {
      toast.error(res?.error || 'Failed to update agent');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="controlled-glass p-10 animate-pulse h-64 rounded-[40px]" />
        <div className="controlled-glass p-10 animate-pulse h-64 rounded-[40px]" />
      </div>
    );
  }

  return (
    <div className="space-y-8 relative pb-20">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div className="flex items-center">
          <Link
            to="/agents"
            className="mr-6 p-3 bg-white/5 border border-white/10 rounded-2xl text-white/40 hover:text-white hover:bg-white/10 transition-all group"
          >
            <ArrowLeftIcon className="h-6 w-6 group-hover:-translate-x-1 transition-transform" />
          </Link>
          <div>
            <h1 className="text-2xl sm:text-2xl font-bold text-white tracking-tight">Edit AI Agent</h1>
            <p className="mt-1 text-sm sm:text-xs font-medium text-white/30 italic">
              Update {name}'s mission and configuration
            </p>
          </div>
        </div>
        <div className="controlled-glass px-5 py-2.5 rounded-2xl border border-primary-500/20 inner-highlight flex items-center space-x-3 self-start sm:self-center">
          <div className="p-1.5 bg-primary-500/10 rounded-lg">
            <SparklesIcon className="h-4 w-4 text-primary-400" />
          </div>
          <span className="text-[10px] font-bold text-white/50 uppercase tracking-widest leading-none">
            Plan: <span className="text-primary-400 font-bold">{subscriptionTier}</span>
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-10 sm:space-y-6">
        <div className="controlled-glass p-8 sm:p-6 rounded-[32px] sm:rounded-2xl inner-highlight">
          <div className="flex items-center space-x-4 mb-8 sm:mb-5">
            <div className="p-2.5 sm:p-1.5 bg-white/5 border border-white/10 rounded-xl sm:rounded-lg">
              <CheckBadge className="h-5 w-5 sm:h-4 w-4 text-blue-400" />
            </div>
            <h3 className="text-xl sm:text-lg font-bold text-white tracking-tight">1. Basic Information</h3>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:gap-5 sm:grid-cols-2">
            <div>
              <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">Agent Name</label>
              <input
                type="text"
                className="input text-base sm:text-sm py-4 sm:py-2.5 opacity-70 cursor-not-allowed bg-white/5"
                value={`${name || ''}`}
                readOnly
              />
            </div>

            <div>
              <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">Support Phone</label>
              <input
                type="tel"
                className={`input text-base sm:text-sm py-4 sm:py-2.5 ${errors.phone ? 'border-red-500/50' : ''}`}
                placeholder="+1 (555) 123-4567"
                {...register('phone')}
              />
              {errors.phone && <p className="mt-2 text-xs font-bold text-red-400 lowercase italic tracking-tight">{errors.phone.message}</p>}
            </div>

            <div>
              <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">Company Name</label>
              <input
                type="text"
                className="input text-base sm:text-sm py-4 sm:py-2.5"
                placeholder="Ex: Apple Inc."
                {...register('companyName')}
              />
            </div>

            <div>
              <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">Admin Email</label>
              <input
                type="text"
                className="input text-base sm:text-sm py-4 sm:py-2.5 opacity-60 bg-white/5 cursor-not-allowed"
                value={`${email || ''}`}
                readOnly
              />
            </div>
          </div>
        </div>

        <div className="controlled-glass p-8 sm:p-6 rounded-[32px] sm:rounded-2xl inner-highlight">
          <div className="flex items-center space-x-4 mb-2">
            <div className="p-2.5 sm:p-1.5 bg-white/5 border border-white/10 rounded-xl sm:rounded-lg">
              <SparklesIcon className="h-5 w-5 sm:h-4 w-4 text-primary-400" />
            </div>
            <h3 className="text-xl sm:text-lg font-bold text-white tracking-tight">2. AI Behavior</h3>
          </div>

          <div className="mt-10 sm:mt-6">
            {featuresAvailable.canEditFirstMessage ? (
              <div>
                <label className="block text-[10px] sm:text-[9px] font-bold text-white/40 uppercase tracking-widest mb-3 sm:mb-1.5 ml-1">
                  Introduction Phrase <span className="text-red-400 font-bold ml-1">*</span>
                </label>
                <div className="relative">
                  <textarea
                    rows={4}
                    className={`input text-base sm:text-sm py-5 sm:py-3 min-h-[140px] sm:min-h-[120px] ${errors.firstMessage ? 'border-red-500/50' : ''}`}
                    placeholder="Ex: Hello! I'm Valoria, how can I help you today?"
                    {...register('firstMessage', { required: 'First message is required' })}
                  />
                  <div className="absolute bottom-4 right-4 text-[10px] font-bold text-white/20 uppercase tracking-widest">
                    {firstMessageValue.length}/500
                  </div>
                </div>
                {errors.firstMessage && <p className="mt-2 text-xs font-bold text-red-400 lowercase italic tracking-tight">{errors.firstMessage.message}</p>}
              </div>
            ) : (
              <UpgradePrompt feature="First Message" currentPlan={subscriptionTier} />
            )}
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end items-center gap-6 sm:gap-4 pt-10 sm:pt-6">
          <Link
            to="/agents"
            className="btn btn-secondary w-full sm:w-auto sm:px-8"
          >
            Cancel Edits
          </Link>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary w-full sm:w-auto sm:px-12"
          >
            <span className="flex items-center justify-center">
              {isSubmitting ? (
                <>
                  <div className="h-4 w-4 border-2 border-white/20 border-t-white rounded-full animate-spin mr-3" />
                  Saving...
                </>
              ) : (
                <>
                  <SparklesIcon className="h-4 w-4" />
                  Update Agent
                </>
              )}
            </span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default AgentEdit;